<template>
    <v-container fluid>
        <v-card-title>
            {{
                isEdit
                    ? $t("addEmployee.titleEdit")
                    : $t("addEmployee.titleAdd")
            }}
        </v-card-title>
        <v-card-text>
            <v-form ref="form">
                <v-row>
                    <!-- ——— Admin-only / Add-only fields ——— -->
                    <template v-if="isAdmin">
                        <!-- First Name -->
                        <v-col cols="12" sm="12" md="6" lg="4">
                            <v-text-field
                                v-model="employee.first_name"
                                :label="$t('addEmployee.firstName')"
                                :rules="nameRules"
                                required
                            />
                        </v-col>

                        <!-- Last Name -->
                        <v-col cols="12" sm="12" md="6" lg="4">
                            <v-text-field
                                v-model="employee.last_name"
                                :label="$t('addEmployee.lastName')"
                                :rules="nameRules"
                                required
                            />
                        </v-col>

                        <!-- Job Title -->
                        <v-col cols="12" sm="12" md="6" lg="4">
                            <v-combobox
                                v-model="jobTitleInput"
                                :items="jobTitleOptions"
                                :label="$t('addEmployee.jobTitle')"
                                :rules="jobTitleRules"
                                clearable
                                hide-details="auto"
                                item-title="label"
                                item-value="value"
                                return-object
                                @update:modelValue="handleJobTitleChange"
                            />
                        </v-col>

                        <!-- Email (disabled on edit) -->
                        <v-col cols="12" sm="12" md="6" lg="4">
                            <v-text-field
                                v-model="employee.email"
                                :label="$t('addEmployee.email')"
                                :rules="emailRules"
                                required
                                :disabled="isEdit"
                            />
                        </v-col>

                        <!-- Hiring proof only on create -->
                        <v-col v-if="!isEdit" cols="12" sm="12" md="6" lg="4">
                            <v-file-input
                                v-model="employee.hiring_proof"
                                :label="$t('addEmployee.hiringProof')"
                                :rules="hiringProofRules"
                                accept=".pdf,.jpg,.png"
                                required
                            />
                        </v-col>
                    </template>

                    <!-- ——— Phone prefix + number always visible if canEdit ——— -->
                    <template v-if="canEdit">
                        <v-col cols="12" sm="8" md="6" lg="4">
                            <v-row no-gutters>
                                <v-col cols="4">
                                    <v-select
                                        v-model="employee.phone_prefix"
                                        :items="europeanCountryCodes"
                                        item-title="code"
                                        item-value="dial_code"
                                        :label="$t('register.prefix')"
                                        hide-details="auto"
                                    />
                                </v-col>
                                <v-col cols="8">
                                    <v-text-field
                                        class="ml-5"
                                        v-model="employee.phone"
                                        :label="$t('addEmployee.phone')"
                                        :rules="phoneNumberRules"
                                        validate-on="input"
                                    >
                                        <template #prepend-inner>
                                            {{ employee.phone_prefix || "" }}
                                        </template>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                        </v-col>
                    </template>
                    <!-- ——— Contact‐via icons (Signal/WhatsApp) – admin only ——— -->
                    <template v-if="isAdmin">
                        <v-col cols="12" xs="4" sm="4" md="4" lg="4">
                            <div class="text-subtitle-2 mb-1">
                                {{ $t("addEmployee.canBeContactedOn") }}
                            </div>
                            <v-row class="flex-nowrap align-center">
                                <v-col cols="2" class="pa-0 mr-6">
                                    <v-checkbox
                                        v-model="employee.hasSignalApp"
                                        hide-details
                                    >
                                        <template #label>
                                            <img
                                                :src="signalIcon"
                                                alt="Signal Icon"
                                                width="20"
                                                height="20"
                                            />
                                        </template>
                                    </v-checkbox>
                                </v-col>
                                <v-col cols="2" class="pa-0">
                                    <v-checkbox
                                        v-model="employee.hasWhatsApp"
                                        hide-details
                                    >
                                        <template #label>
                                            <v-icon color="green"
                                                >mdi-whatsapp</v-icon
                                            >
                                        </template>
                                    </v-checkbox>
                                </v-col>
                            </v-row>
                        </v-col>
                    </template>
                    <!-- ——— Extra phones ——— -->
                    <!-- phones – one v-row per phone, just like e-mails -->
                    <template v-if="canEdit">
                        <v-col cols="12">
                            <!-- Title + “add” button that’s always visible -->
                            <v-card class="mb-4">
                                <v-toolbar density="comfortable">
                                    <v-toolbar-title>{{
                                        $t("addEmployee.extraPhones")
                                    }}</v-toolbar-title>

                                    <v-spacer />

                                    <v-btn
                                        icon="mdi-plus"
                                        elevation="0"
                                        @click="addPhone(employee)"
                                    />
                                </v-toolbar>

                                <v-card-text>
                                    <!-- ======= DESKTOP (md and up) – one big row with cols ======= -->
                                    <template
                                        v-if="!$vuetify.display.mdAndDown"
                                    >
                                        <v-row
                                            v-for="(p, idx) in employee.phones"
                                            :key="`phone-row-${idx}`"
                                            class="align-center mb-2"
                                            dense
                                        >
                                            <v-col cols="2">
                                                <v-select
                                                    v-model="p.prefix"
                                                    :rules="[
                                                        () =>
                                                            $t(
                                                                'addEmployee.note'
                                                            ),
                                                    ]"
                                                    :items="
                                                        europeanCountryCodes
                                                    "
                                                    item-title="code"
                                                    item-value="dial_code"
                                                    :label="
                                                        $t('register.prefix')
                                                    "
                                                    density="compact"
                                                    hide-details="auto"
                                                />
                                            </v-col>

                                            <v-col cols="2">
                                                <v-text-field
                                                    v-model="p.number"
                                                    :label="
                                                        $t('addEmployee.phone')
                                                    "
                                                    density="compact"
                                                    hide-details
                                                />
                                            </v-col>

                                            <v-col cols="3">
                                                <v-text-field
                                                    :counter="
                                                        p.notes.length > 75
                                                            ? 150
                                                            : false
                                                    "
                                                    v-model="p.notes"
                                                    :rules="[
                                                        (v) =>
                                                            (v &&
                                                                v.length <=
                                                                    150) ||
                                                            $t(
                                                                'addEmployee.note'
                                                            ),
                                                    ]"
                                                    :label="
                                                        $t('addEmployee.notes')
                                                    "
                                                    density="compact"
                                                    hide-details="auto"
                                                />
                                            </v-col>

                                            <v-col cols="2">
                                                <v-checkbox
                                                    v-model="p.has_whatsapp"
                                                    label="WhatsApp"
                                                    density="compact"
                                                    hide-details
                                                />
                                            </v-col>

                                            <v-col cols="2">
                                                <v-checkbox
                                                    v-model="p.has_signal"
                                                    label="Signal"
                                                    density="compact"
                                                    hide-details
                                                />
                                            </v-col>

                                            <v-col cols="1">
                                                <v-btn
                                                    icon="mdi-delete"
                                                    elevation="3"
                                                    density="compact"
                                                    color="error"
                                                    @click="
                                                        employee.phones.splice(
                                                            idx,
                                                            1
                                                        )
                                                    "
                                                />
                                            </v-col>
                                        </v-row>
                                    </template>

                                    <!-- ======= TABLET / MOBILE (sm & down) – each phone in its own card ======= -->
                                    <template v-else>
                                        <v-row>
                                            <v-col
                                                v-for="(
                                                    p, idx
                                                ) in employee.phones"
                                                :key="`phone-card-${idx}`"
                                                cols="12"
                                            >
                                                <v-card
                                                    elevation="1"
                                                    class="mb-3"
                                                >
                                                    <v-toolbar>
                                                        <v-toolbar-title>
                                                            {{
                                                                $t(
                                                                    "addEmployee.phone"
                                                                )
                                                            }}
                                                            {{ idx + 1 }}
                                                        </v-toolbar-title>
                                                        <v-toolbar-items>
                                                            <v-btn
                                                                icon="mdi-delete"
                                                                variant="text"
                                                                color="error"
                                                                elevation="0"
                                                                @click="
                                                                    employee.phones.splice(
                                                                        idx,
                                                                        1
                                                                    )
                                                                "
                                                            />
                                                        </v-toolbar-items>
                                                    </v-toolbar>

                                                    <v-card-text>
                                                        <v-select
                                                            v-model="p.prefix"
                                                            :items="
                                                                europeanCountryCodes
                                                            "
                                                            item-title="code"
                                                            item-value="dial_code"
                                                            :label="
                                                                $t(
                                                                    'register.prefix'
                                                                )
                                                            "
                                                            density="compact"
                                                            hide-details
                                                            class="mb-2"
                                                        />

                                                        <v-text-field
                                                            v-model="p.number"
                                                            :label="
                                                                $t(
                                                                    'addEmployee.phone'
                                                                )
                                                            "
                                                            density="compact"
                                                            hide-details
                                                            class="mb-2"
                                                        />

                                                        <v-text-field
                                                            v-model="p.notes"
                                                            :counter="
                                                                p.notes.length >
                                                                75
                                                                    ? 150
                                                                    : false
                                                            "
                                                            :rules="[
                                                                (v) =>
                                                                    (v &&
                                                                        v.length <=
                                                                            150) ||
                                                                    $t(
                                                                        'addEmployee.note'
                                                                    ),
                                                            ]"
                                                            :label="
                                                                $t(
                                                                    'addEmployee.notes'
                                                                )
                                                            "
                                                            density="compact"
                                                            hide-details="auto"
                                                            class="mb-2"
                                                        />

                                                        <v-checkbox
                                                            v-model="
                                                                p.has_whatsapp
                                                            "
                                                            label="WhatsApp"
                                                            density="compact"
                                                            hide-details
                                                        />

                                                        <v-checkbox
                                                            v-model="
                                                                p.has_signal
                                                            "
                                                            label="Signal"
                                                            density="compact"
                                                            hide-details
                                                        />
                                                    </v-card-text>
                                                </v-card>
                                            </v-col>
                                        </v-row>
                                    </template>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </template>
                    <template v-if="canEdit">
                        <v-col cols="12" class="mt-6">
                            <v-card>
                                <v-toolbar density="comfortable">
                                    <v-toolbar-title>{{
                                        $t("addEmployee.extraEmails")
                                    }}</v-toolbar-title>

                                    <v-spacer />

                                    <v-btn
                                        icon="mdi-plus"
                                        elevation="0"
                                        @click="addEmail(employee)"
                                    />
                                </v-toolbar>

                                <v-card-text>
                                    <!-- ======= DESKTOP (md and up) – grid row ======= -->
                                    <template
                                        v-if="!$vuetify.display.mdAndDown"
                                    >
                                        <v-row
                                            v-for="(
                                                e, idx
                                            ) in employee.alt_emails"
                                            :key="`email-row-${idx}`"
                                            class="align-center mb-2"
                                            dense
                                        >
                                            <!-- e-mail -->
                                            <v-col cols="6" sm="4">
                                                <v-text-field
                                                    v-model="e.email"
                                                    :label="
                                                        $t('addEmployee.email')
                                                    "
                                                    density="compact"
                                                    hide-details
                                                />
                                            </v-col>

                                            <!-- notes -->
                                            <v-col cols="4" sm="3">
                                                <v-text-field
                                                    v-model="e.notes"
                                                    :counter="
                                                        e.notes.length >
                                                        75
                                                            ? 150
                                                            : false
                                                    "
                                                    :rules="[
                                                        (v) =>
                                                            (v &&
                                                                v.length <=
                                                                    150) ||
                                                            $t(
                                                                'addEmployee.note'
                                                            ),
                                                    ]"
                                                    :label="
                                                        $t('addEmployee.notes')
                                                    "
                                                    density="compact"
                                                    hide-details="auto"
                                                />
                                            </v-col>

                                            <!-- remove -->
                                            <v-col cols="auto">
                                                <v-btn
                                                    icon="mdi-delete"
                                                    density="compact"
                                                    color="error"
                                                    @click="
                                                        employee.alt_emails.splice(
                                                            idx,
                                                            1
                                                        )
                                                    "
                                                />
                                            </v-col>
                                        </v-row>
                                    </template>

                                    <!-- ======= TABLET / MOBILE (md and down) – one card per e-mail ======= -->
                                    <template v-else>
                                        <v-row>
                                            <v-col
                                                v-for="(
                                                    e, idx
                                                ) in employee.alt_emails"
                                                :key="`email-card-${idx}`"
                                                cols="12"
                                            >
                                                <v-card
                                                    elevation="1"
                                                    class="mb-3"
                                                >
                                                    <v-toolbar
                                                        density="comfortable"
                                                    >
                                                        <v-toolbar-title>
                                                            {{
                                                                $t(
                                                                    "addEmployee.email"
                                                                )
                                                            }}
                                                            {{ idx + 1 }}
                                                        </v-toolbar-title>
                                                        <v-toolbar-items>
                                                            <v-btn
                                                                icon="mdi-delete"
                                                                color="error"
                                                                elevation="0"
                                                                @click="
                                                                    employee.alt_emails.splice(
                                                                        idx,
                                                                        1
                                                                    )
                                                                "
                                                            />
                                                        </v-toolbar-items>
                                                    </v-toolbar>

                                                    <v-card-text>
                                                        <v-text-field
                                                            v-model="e.email"
                                                            :label="
                                                                $t(
                                                                    'addEmployee.email'
                                                                )
                                                            "
                                                            density="compact"
                                                            hide-details
                                                            class="mb-2"
                                                        />

                                                        <v-text-field
                                                            v-model="e.notes"
                                                            :counter="
                                                                e.notes.length >
                                                                75
                                                                    ? 150
                                                                    : false
                                                            "
                                                            :rules="[
                                                                (v) =>
                                                                    (v &&
                                                                        v.length <=
                                                                            150) ||
                                                                    $t(
                                                                        'addEmployee.note'
                                                                    ),
                                                            ]"
                                                            :label="
                                                                $t(
                                                                    'addEmployee.notes'
                                                                )
                                                            "
                                                            density="compact"
                                                            hide-details="auto"
                                                        />
                                                    </v-card-text>
                                                </v-card>
                                            </v-col>
                                        </v-row>
                                    </template>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </template>
                    <!-- ——— Two new email‐preferences always visible if canEdit ——— -->
                    <template v-if="canEdit">
                        <v-col cols="12" xs="4" sm="4" md="4" lg="4">
                            <v-switch
                                v-model="employee.should_receive_truck_emails"
                                :label="$t('addEmployee.truckEmails')"
                                :color="
                                    employee.should_receive_truck_emails
                                        ? 'green'
                                        : 'grey'
                                "
                            />
                        </v-col>
                        <v-col cols="12" xs="4" sm="4" md="4" lg="4">
                            <v-switch
                                v-model="employee.should_receive_ware_emails"
                                :label="$t('addEmployee.wareEmails')"
                                :color="
                                    employee.should_receive_ware_emails
                                        ? 'green'
                                        : 'grey'
                                "
                            />
                        </v-col>
                        <v-col cols="12" sm="4" md="4" lg="4">
                            <SelectSpokenLanguages
                                v-model="employee.spoken_languages"
                            />
                        </v-col>
                    </template>
                </v-row>
            </v-form>
        </v-card-text>
    </v-container>
</template>

<script>
import axios from "axios";
import { mapMutations } from "vuex";
import emitter from "@/eventBus";
import signalIcon from "@/assets/signal.svg";
import { h } from "vue";
import countryCodes from "@/locales/countryCodes.json";
import SelectSpokenLanguages from "@/components/SelectSpokenLanguages.vue";
import { showAlertModal } from "@/utils";

export default {
    name: "AddEmployee",
    components: {
        SelectSpokenLanguages,
    },
    data() {
        return {
            jobTitleInput: "",
            countryCodes,
            signalIcon,
            isEdit: false,
            employee: {
                id: null,
                first_name: "",
                last_name: "",
                job_title: "",
                email: "",
                phone_prefix: "",
                phone: "",
                hiring_proof: null,
                hasSignalApp: false,
                hasWhatsApp: false,
                should_receive_truck_emails: false,
                should_receive_ware_emails: false,
                spoken_languages: [],
                phones: [],
                alt_emails: [],
            },
            phonePrefix: {
                code: "RO",
                dial_code: "+40",
            },
            phoneNumber: "",
        };
    },
    computed: {
        jobTitleOptions() {
            return [
                { value: "expeditor", label: this.$t("jobTitle.expeditor") },
                {
                    value: "sefDepartament",
                    label: this.$t("jobTitle.sefDepartament"),
                },
                { value: "contabil", label: this.$t("jobTitle.contabil") },
            ];
        },
        spokenLanguagesOptions() {
            return [
                { value: "ro", label: this.$t("language.ro") },
                { value: "en", label: this.$t("language.en") },
                { value: "de", label: this.$t("language.de") },
                { value: "fr", label: this.$t("language.fr") },
                { value: "hu", label: this.$t("language.hu") },
            ];
        },
        currentUser() {
            return JSON.parse(sessionStorage.getItem("user") || "{}");
        },
        isAdmin() {
            return this.currentUser.role === "admin";
        },
        isSelf() {
            return (
                this.isEdit &&
                Number(this.$route.params.id) === this.currentUser.id
            );
        },
        // admin can edit anyone; employee only themselves
        canEdit() {
            return this.isAdmin || this.isSelf;
        },
        europeanCountryCodes() {
            const eu = [
                "RO",
                "DE",
                "FR",
                "IT",
                "ES",
                "PL",
                "BG",
                "NL",
                "BE",
                "HU",
                "AT",
                "SE",
                "FI",
                "DK",
                "IE",
                "CZ",
                "SK",
                "HR",
                "GR",
                "PT",
                "SI",
                "LU",
                "LT",
                "LV",
                "EE",
                "CY",
                "MT",
                "NO",
                "CH",
                "GB",
                "IS",
                "AL",
                "ME",
                "MK",
                "MD",
                "UA",
            ];
            return countryCodes.filter((c) => eu.includes(c.code));
        },
        getCountryDialCode() {
            return this.phonePrefix?.dial_code || "";
        },
        submitRequested() {
            return this.$store.state.submitRequested;
        },
        phoneNumberRules() {
            return [
                (v) => !!v || this.$t("addEmployee.validation.required"),
                (v) =>
                    /^[0-9]{8,}$/.test(v) ||
                    this.$t("addEmployee.validation.invalidPhone"),
            ];
        },
        nameRules() {
            return [
                (v) => !!v || this.$t("addEmployee.validation.required"),
                (v) =>
                    (v && v.length >= 2) ||
                    this.$t("addEmployee.validation.minLength"),
            ];
        },
        emailRules() {
            return [
                (v) => !!v || this.$t("addEmployee.validation.required"),
                (v) =>
                    /.+@.+\..+/.test(v) ||
                    this.$t("addEmployee.validation.invalidEmail"),
            ];
        },
        phoneRules() {
            return [
                (v) => !!v || this.$t("addEmployee.validation.required"),
                (v) =>
                    /^[0-9+\s()-]+$/.test(v) ||
                    this.$t("addEmployee.validation.invalidPhone"),
            ];
        },
        jobTitleRules() {
            return [
                () =>
                    !!this.employee.job_title || this.$t("validation.required"),
            ];
        },

        hiringProofRules() {
            return [
                (v) =>
                    (v && v.length > 0) ||
                    this.$t("addEmployee.validation.required"),
            ];
        },
    },
    created() {
        // If there's an :id in the route, we're in "edit" mode.
        if (this.$route.params.id) {
            this.isEdit = true;
            this.fetchEmployee(this.$route.params.id);
        }
    },
    mounted() {
        this.languageChangeListener = () => {
            this.updateJobTitleInputTranslation();
        };
        emitter.on("language-changed", this.languageChangeListener);
        this.SET_FORM_BUTTONS([
            {
                label: "common.save",
                event: "save-employee",
                color: "primary",
            },
        ]);
        emitter.on("save-employee", this.saveEmployee);
        emitter.on("language-changed", this.revalidateForm);
    },
    beforeUnmount() {
        emitter.off("language-changed", this.revalidateForm);
        emitter.off("language-changed", this.languageChangeListener);
        emitter.off("save-employee", this.saveEmployee);
        this.RESET_FORM_BUTTONS();
    },
    methods: {
        ...mapMutations(["SET_FORM_BUTTONS", "RESET_FORM_BUTTONS"]),
        showAlertModal,
        addPhone(employee) {
            employee.phones.push({
                prefix: "",
                number: "",
                notes: "",
                has_whatsapp: false,
                has_signal: false,
            });
        },
        addEmail(employee) {
            employee.alt_emails.push({
                email: "",
                notes: "",
            });
        },
        revalidateForm() {
            this.$refs.form?.validate();
        },
        updateJobTitleInputTranslation() {
            const match = this.jobTitleOptions.find(
                (opt) => opt.value === this.employee.job_title
            );
            if (match) {
                this.jobTitleInput = this.$t(`jobTitle.${match.value}`);
            }
        },
        handleJobTitleChange(selected) {
            if (typeof selected === "object" && selected !== null) {
                this.jobTitleInput = selected.label;
                this.employee.job_title = selected.value;
            } else {
                this.jobTitleInput = selected;
                this.employee.job_title = selected;
            }
        },
        /**
         * Fetch an existing employee when editing.
         */
        async fetchEmployee(employeeId) {
            try {
                const response = await axios.get(
                    `${process.env.VUE_APP_API_URL}/employees/${employeeId}/`
                );
                if (response.status === 200) {
                    // Fill local employee state
                    Object.assign(this.employee, response.data);
                    // Set job title input
                    const match = this.jobTitleOptions.find(
                        (opt) => opt.value === this.employee.job_title
                    );
                    if (match) {
                        this.jobTitleInput = this.$t(`jobTitle.${match.value}`);
                    } else {
                        this.jobTitleInput = this.employee.job_title; // free text
                    }
                    this.employee.phone_prefix =
                        response.data.phone_prefix || "+40";
                    this.employee.phone = response.data.phone || "";
                }
            } catch (error) {
                console.error("Error fetching employee:", error);
                this.showAlertModal(
                    this.$store,
                    "Error fetching employee",
                    "danger",
                    12000,
                    error
                );
            }
        },

        /**
         * Handles both add (POST) and update (PUT).
         */
        async saveEmployee() {
            if (!this.canEdit) {
                this.showAlertModal(
                    this.$store,
                    this.$t("addEmployee.notAllowed"),
                    "warning",
                    6000
                );
                return;
            }

            const isValid = await this.$refs.form.validate();
            if (!isValid.valid) return;

            const userData = sessionStorage.getItem("user");
            if (!userData) {
                this.showAlertModal(
                    this.$store,
                    this.$t("addEmployee.noUserData"),
                    "danger",
                    12000
                );
                return;
            }

            const currentUser = JSON.parse(userData);
            const companyId = currentUser.company;
            const normalizedPhoneNumber = this.employee.phone
                .replace(/[\s-]+/g, "")
                .replace(/^0+/, "");
            const phone = normalizedPhoneNumber;

            if (!this.isEdit) {
                // ---- CREATE (POST) ----
                const formData = new FormData();
                formData.append("first_name", this.employee.first_name);
                formData.append("last_name", this.employee.last_name);
                formData.append("email", this.employee.email);
                formData.append("company", companyId);
                formData.append("role", "employee");
                if (phone) {
                    formData.append("phone_prefix", this.employee.phone_prefix);
                    formData.append("phone", normalizedPhoneNumber);
                }
                this.employee.phones.forEach((p, i) => {
                    if (p.number) {
                        formData.append(`phones[${i}].prefix`, p.prefix);
                        formData.append(`phones[${i}].number`, p.number);
                        formData.append(`phones[${i}].notes`, p.notes);
                        formData.append(
                            `phones[${i}].has_whatsapp`,
                            p.has_whatsapp
                        );
                        formData.append(
                            `phones[${i}].has_signal`,
                            p.has_signal
                        );
                    }
                });

                this.employee.alt_emails.forEach((e, i) => {
                    formData.append(`alt_emails[${i}].email`, e.email);
                    formData.append(`alt_emails[${i}].notes`, e.notes);
                });
                if (this.employee.job_title)
                    formData.append("job_title", this.employee.job_title);
                if (this.employee.hiring_proof)
                    formData.append("hiring_proof", this.employee.hiring_proof);
                formData.append("hasSignalApp", this.employee.hasSignalApp);
                formData.append("hasWhatsApp", this.employee.hasWhatsApp);
                formData.append(
                    "should_receive_truck_emails",
                    this.employee.should_receive_truck_emails
                );
                formData.append(
                    "should_receive_ware_emails",
                    this.employee.should_receive_ware_emails
                );
                this.employee.spoken_languages.forEach((lang) =>
                    formData.append("spoken_languages", lang)
                );

                try {
                    const res = await axios.post(
                        `${process.env.VUE_APP_API_URL}/employees/`,
                        formData,
                        {
                            headers: { "Content-Type": "multipart/form-data" },
                        }
                    );
                    this.showAlertModal(
                        this.$store,
                        `${this.$t("addEmployee.success")} ${this.$t(
                            "addEmployee.passwordResetLink"
                        )}: ${res.data.reset_link}`,
                        "success",
                        12000
                    );
                    this.$router.push("/dashboard/employees");
                } catch (err) {
                    console.error("Create error:", err);
                    this.showAlertModal(
                        this.$store,
                        this.$t("addEmployee.error"),
                        "danger",
                        12000
                    );
                }
            } else {
                // ---- UPDATE (PATCH) ----
                const payload = {
                    should_receive_truck_emails:
                        this.employee.should_receive_truck_emails,
                    should_receive_ware_emails:
                        this.employee.should_receive_ware_emails,
                    hasSignalApp: this.employee.hasSignalApp,
                    hasWhatsApp: this.employee.hasWhatsApp,
                    spoken_languages: this.employee.spoken_languages,
                    phones: this.employee.phones.filter(
                        (phone) => phone.number && phone.prefix
                    ),
                    alt_emails: this.employee.alt_emails.filter(
                        (altEmail) => altEmail.email
                    ),
                };

                if (phone) {
                    payload.phone_prefix = this.employee.phone_prefix;
                    payload.phone = normalizedPhoneNumber;
                }
                if (this.isAdmin) {
                    payload.job_title = this.employee.job_title;
                    payload.first_name = this.employee.first_name;
                    payload.last_name = this.employee.last_name;
                    payload.email = this.employee.email;
                }

                try {
                    await axios.patch(
                        `${process.env.VUE_APP_API_URL}/employees/${this.employee.id}/`,
                        payload,
                        {
                            headers: { "Content-Type": "application/json" },
                        }
                    );
                    this.showAlertModal(
                        this.$store,
                        this.$t("addEmployee.updateSuccess"),
                        "success",
                        5000
                    );
                    this.$router.push("/dashboard/employees");
                } catch (err) {
                    console.error("Update error:", err);
                    this.showAlertModal(
                        this.$store,
                        this.$t("addEmployee.error"),
                        "danger",
                        12000,
                        err
                    );
                }
            }
        },
    },
    watch: {
        submitRequested(newVal) {
            if (newVal) {
                this.saveEmployee();
                // Reset the flag after submission.
                this.$store.commit("RESET_SUBMIT");
            }
        },
    },
};
</script>

<style scoped>
/* Component-specific styles */
</style>
